import React, { useState, useEffect } from 'react';
import { ThemeProvider, createTheme, CssBaseline, Box } from '@mui/material';
import LandingPage from './components/LandingPage';
import Dashboard from './components/Dashboard';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsAndConditions from './components/TermsAndConditions';
import PublicStorePage from './components/PublicStorePage';
import GuestDiscoveryPage from './components/GuestDiscoveryPage';
import { authAPI } from './api';
import { RADIUS, TOUCH } from './theme/tokens';

// Brand type: Plus Jakarta Sans for headings, Manrope for body/UI.
const HEADING_FONT = '"Plus Jakarta Sans", "Manrope", "Helvetica", "Arial", sans-serif';
const BODY_FONT = '"Manrope", "Helvetica", "Arial", sans-serif';

const makeTheme = (mode) => createTheme({
  palette: {
    mode,
    // Two-tone audience system: blue = vendor, orange = customer/resident.
    primary: { main: '#2563EB', dark: '#1E3A8A' },
    secondary: { main: '#E8770C', dark: '#B45309' },
    // Semantic status colors — use color="success|error|warning|info" instead of
    // hardcoding greens/reds per screen. .light/.dark are auto-derived by MUI so
    // existing `primary.light`-style references keep working.
    success: { main: '#2E7D32', dark: '#1B5E20' },
    error: { main: '#D32F2F', dark: '#C62828' },
    warning: { main: '#ED6C02', dark: '#E65100' },
    info: { main: '#0288D1', dark: '#0277BD' },
  },
  // A real scale, sized for a 390px phone. MUI's stock sizes are desktop sizes, which is why
  // screens used to bypass the variants and hand-set fontSize — 40 distinct values across the
  // app, seven of them inside a 2.4px band. Reach for a variant, not a fontSize.
  typography: {
    fontFamily: BODY_FONT,
    h1: { fontFamily: HEADING_FONT, fontWeight: 800 },
    h2: { fontFamily: HEADING_FONT, fontWeight: 800 },
    h3: { fontFamily: HEADING_FONT, fontWeight: 800 },
    h4: { fontFamily: HEADING_FONT, fontWeight: 800 },
    h5: { fontFamily: HEADING_FONT, fontWeight: 800 },
    h6: { fontFamily: HEADING_FONT, fontWeight: 700, fontSize: '1.0625rem' },
    subtitle1: { fontFamily: HEADING_FONT, fontWeight: 700, fontSize: '0.9375rem' }, // card titles
    body1: { fontSize: '0.9375rem' },
    body2: { fontSize: '0.8125rem' },   // the workhorse
    caption: { fontSize: '0.75rem' },
    overline: { fontSize: '0.6875rem', fontWeight: 700, letterSpacing: 0.5 },
    button: { fontWeight: 600, fontSize: '0.8125rem' },
  },
  components: {
    // A floor under the small button, which is what dense screens reach for and what MUI
    // ships at ~30px — too small to hit reliably on a phone. Deliberately not a blanket
    // `root` floor: that would silently retune every button on every screen at once. Screens
    // that need the full 44px target say so themselves (see TOUCH in theme/tokens.js).
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', borderRadius: RADIUS.sm },
        sizeSmall: { minHeight: TOUCH.dense, paddingTop: 4, paddingBottom: 4 },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': { borderRadius: RADIUS.sm },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { borderRadius: RADIUS.md },
      },
    },
    // App bars are consistently a flat, neutral surface — default here so
    // screens stop repeating color="default" elevation={1}.
    MuiAppBar: {
      defaultProps: { color: 'default', elevation: 1 },
    },
    MuiCard: {
      styleOverrides: {
        root: { borderRadius: RADIUS.md },
      },
    },
    // Status labels are read at a glance by vendors — keep them bold and
    // legible everywhere.
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600 },
      },
    },
    // One brand voice for every dialog heading instead of ad-hoc per screen.
    MuiDialogTitle: {
      styleOverrides: {
        root: { fontFamily: HEADING_FONT, fontWeight: 700, fontSize: '1.15rem' },
      },
    },
  },
});

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [themeMode, setThemeMode] = useState(
    () => localStorage.getItem('themeMode') || 'light'
  );

  const theme = makeTheme(themeMode);

  const handleToggleTheme = () => {
    setThemeMode(prev => {
      const next = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem('themeMode', next);
      return next;
    });
  };

  useEffect(() => {
    // Check if user is already logged in by validating the token
    const validateToken = async () => {
      const token = localStorage.getItem('access_token');
      if (token) {
        try {
          // Try to fetch user data to validate token
          await authAPI.getCurrentUser();
          setIsAuthenticated(true);
        } catch (error) {
          // Token is invalid or expired
          console.log('Token validation failed:', error);
          localStorage.removeItem('access_token');
          localStorage.removeItem('user_id');
          localStorage.removeItem('mobile');
          setIsAuthenticated(false);
        }
      }
      setIsLoading(false);
    };

    validateToken();
  }, []);

  const handleLoginSuccess = (userData) => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
  };

  if (isLoading) {
    return null;
  }

  // Standalone public pages (no auth required)
  const path = window.location.pathname;
  if (path.startsWith('/store/')) {
    const businessId = parseInt(path.split('/')[2]);
    if (businessId) {
      return (
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <PublicStorePage businessId={businessId} />
        </ThemeProvider>
      );
    }
  }
  if (path === '/discover') {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GuestDiscoveryPage />
      </ThemeProvider>
    );
  }
  if (path === '/privacy') {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <PrivacyPolicy />
      </ThemeProvider>
    );
  }
  if (path === '/terms') {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <TermsAndConditions />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      <Box>
        {isAuthenticated ? (
          <Dashboard onLogout={handleLogout} themeMode={themeMode} onToggleTheme={handleToggleTheme} />
        ) : (
          <LandingPage onLoginSuccess={handleLoginSuccess} />
        )}
      </Box>

    </ThemeProvider>
  );
}

export default App;